import { useLayoutEffect, type RefObject } from "react";
import { useLocation, useNavigate, type NavigateOptions } from "react-router";

export type NavDirection = "push" | "pop" | "lateral" | "none";

interface DirectionalNavigateOptions extends NavigateOptions {
  direction?: NavDirection;
}

const ENTER_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

function readDirection(state: unknown): NavDirection {
  return (state as { direction?: NavDirection } | null)?.direction ?? "none";
}

export function useDirectionalNavigate() {
  const navigate = useNavigate();

  return (to: string, options?: DirectionalNavigateOptions) => {
    const { direction = "none", state, ...rest } = options ?? {};
    navigate(to, { ...rest, state: { ...(state ?? {}), direction } });
  };
}

/**
 * Plays the directional enter animation for the given direction; `none` is a no-op.
 */
export function playDirectionalEnter(element: HTMLElement, direction: NavDirection): void {
  if (direction === "none") return;

  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const offset = reduce || direction === "lateral" ? "0px" : direction === "pop" ? "-12px" : "12px";

  element.animate(
    [
      { opacity: 0, transform: `translateX(${offset})` },
      { opacity: 1, transform: "translateX(0)" },
    ],
    { duration: reduce ? 100 : direction === "lateral" ? 140 : 160, easing: ENTER_EASING },
  );
}

export function usePageEnterAnimation(ref: RefObject<HTMLElement | null>): void {
  const location = useLocation();

  useLayoutEffect(() => {
    if (ref.current) playDirectionalEnter(ref.current, readDirection(location.state));
  }, [location, ref]);
}
